import { useCallback, useEffect, useRef, useState } from "react";
import { readWorldGoals, type WorldGoalRow } from "./worldSessionClient";
export const GOALS_CHANGED = "neucleos-goals-changed";
// The last answer per World, so a reopened chooser shows its goals at once
// and refreshes behind them instead of opening on an empty loading screen.
const lastGoals = new Map<string, ReadonlyArray<WorldGoalRow>>();
export function useWorldGoals(worldKey: string | null) {
  const request = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const [address, setAddress] = useState(worldKey);
  const [goals, setGoals] = useState<ReadonlyArray<WorldGoalRow>>(
    () => (worldKey === null ? undefined : lastGoals.get(worldKey)) ?? [],
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(worldKey !== null && !lastGoals.has(worldKey));
  const refresh = useCallback(async () => {
    const sequence = ++request.current;
    controller.current?.abort();
    if (!worldKey) {
      setError(null);
      setGoals([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const pending = new AbortController();
    controller.current = pending;
    const result = await readWorldGoals(worldKey, fetch, pending.signal);
    if (sequence !== request.current) return;
    if (result.ok) {
      lastGoals.set(worldKey, result.goals);
      setGoals(result.goals);
      setError(null);
    } else setError(result.reason);
    setLoading(false);
  }, [worldKey]);
  useEffect(() => {
    setAddress(worldKey);
    setGoals((worldKey === null ? undefined : lastGoals.get(worldKey)) ?? []);
    setError(null);
    void refresh();
    const listener = () => void refresh();
    window.addEventListener(GOALS_CHANGED, listener);
    return () => {
      request.current++;
      controller.current?.abort();
      window.removeEventListener(GOALS_CHANGED, listener);
    };
  }, [refresh, worldKey]);
  const current = address === worldKey;
  return {
    goals: current ? goals : [],
    error: current ? error : null,
    // A cached answer is shown while it refreshes; only a first read loads.
    loading: worldKey !== null && (!current || (loading && !lastGoals.has(worldKey))),
    refresh,
    setGoals,
    setError,
  };
}
export function notifyGoalsChanged() {
  window.dispatchEvent(new Event(GOALS_CHANGED));
}
export function goalParents(goal: WorldGoalRow) {
  return goal.parentIds ?? (goal.parent ? [goal.parent] : []);
}
export function descendantsOf(goals: ReadonlyArray<WorldGoalRow>, roots: ReadonlyArray<string>) {
  const children = new Map<string, string[]>();
  for (const goal of goals) {
    for (const parent of goalParents(goal)) {
      const siblings = children.get(parent);
      if (siblings) siblings.push(goal.goalId);
      else children.set(parent, [goal.goalId]);
    }
  }
  const included = new Set(roots);
  const queue = [...included];
  for (let index = 0; index < queue.length; index++) {
    for (const child of children.get(queue[index]!) ?? []) {
      if (included.has(child)) continue;
      included.add(child);
      queue.push(child);
    }
  }
  return included;
}
